import { createFileRoute, redirect, useNavigate, Link } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { fetchArticleMetadata } from "@/lib/fetch-article-metadata.functions";
import {
  uploadArticleMedia,
  validateImageFile,
  ACCEPTED_UPLOAD_ACCEPT,
  type ArticleMediaKind,
  type CropSpec,
} from "@/lib/article-image";
const ImageCropDialog = lazy(() =>
  import("@/components/redaction/ImageCropDialog").then((m) => ({ default: m.ImageCropDialog })),
);
const MfaSetup = lazy(() => import("@/components/MfaSetup").then((m) => ({ default: m.MfaSetup })));


import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
const ParcoursSection = lazy(() =>
  import("@/components/redaction/ParcoursSection").then((m) => ({ default: m.ParcoursSection })),
);
import { ContentViewsCell } from "@/components/redaction/ContentViews";
import { HighlightsSection } from "@/components/redaction/HighlightsSection";

const ArticlesSection = lazy(() =>
  import("@/components/redaction/sections/ArticlesSection").then((m) => ({ default: m.ArticlesSection })),
);
const MediasSection = lazy(() =>
  import("@/components/redaction/sections/MediasSection").then((m) => ({ default: m.MediasSection })),
);
const DatavizSection = lazy(() =>
  import("@/components/redaction/sections/DatavizSection").then((m) => ({ default: m.DatavizSection })),
);
const InstagramSection = lazy(() =>
  import("@/components/redaction/sections/InstagramSection").then((m) => ({ default: m.InstagramSection })),
);
const PodcastSection = lazy(() =>
  import("@/components/redaction/sections/PodcastSection").then((m) => ({ default: m.PodcastSection })),
);
const ProjetsSection = lazy(() =>
  import("@/components/redaction/sections/ProjetsSection").then((m) => ({ default: m.ProjetsSection })),
);

const DATAWRAPPER_URL_PREFIX = "https://datawrapper.dwcdn.net/";

/** Return the URL if it's a well-formed Datawrapper embed URL, otherwise null. */
function normalizeDatawrapperUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith(DATAWRAPPER_URL_PREFIX)) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "https:") return null;
    if (u.hostname !== "datawrapper.dwcdn.net") return null;
    return u.toString();
  } catch {
    return null;
  }
}

export const Route = createFileRoute("/redaction/dashboard")({
  ssr: false,
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { property: "og:description", content: "Espace d’administration des contenus du site de Mathys Gautier." },
      { property: "og:title", content: "Tableau de bord de la rédaction — Mathys Gautier" },
      { name: "description", content: "Espace d’administration des contenus du site de Mathys Gautier." },
      { title: "Tableau de bord de la rédaction — Mathys Gautier" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/redaction" });
    }
    // Double authentification : si un facteur TOTP est enrôlé, la session doit
    // être élevée en aal2 (code à 6 chiffres validé) pour accéder au dashboard.
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") {
      throw redirect({ to: "/redaction" });
    }
    return { user: data.user };
  },

  component: Dashboard,
});

function Dashboard() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/redaction", replace: true });
  }

  // Déconnexion automatique après 30 min sans activité.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void handleSignOut(), 30 * 60 * 1000);
    };
    const events = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="mx-auto max-w-6xl px-6 py-10">
      <div className="mb-6">
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
        >
          ← Retour au site
        </Link>
      </div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tableau de bord</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Connecté en tant qu'administrateur ({user.email})
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleSignOut}>
          Se déconnecter
        </Button>
      </div>

      <div className="mt-6">
        <Suspense fallback={null}>
          <MfaSetup />
        </Suspense>
      </div>



      <Suspense fallback={<Loader2 className="mt-8 h-5 w-5 animate-spin" />}>
      <Tabs defaultValue="medias" className="mt-8">
        <TabsList>
          <TabsTrigger value="medias">Médias</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="dataviz">Dataviz</TabsTrigger>
          <TabsTrigger value="projets">Projets</TabsTrigger>
          <TabsTrigger value="parcours">Parcours</TabsTrigger>
          <TabsTrigger value="highlights">Highlights</TabsTrigger>
        </TabsList>
        <TabsContent value="medias" className="mt-6">
          <MediasSection />
        </TabsContent>
        <TabsContent value="portfolio" className="mt-6">
          <Tabs defaultValue="articles">
            <TabsList>
              <TabsTrigger value="articles">Articles</TabsTrigger>
              <TabsTrigger value="instagram">Instagram</TabsTrigger>
              <TabsTrigger value="podcast">Podcast</TabsTrigger>
            </TabsList>
            <TabsContent value="articles" className="mt-6">
              <ArticlesSection />
            </TabsContent>
            <TabsContent value="instagram" className="mt-6">
              <InstagramSection />
            </TabsContent>
            <TabsContent value="podcast" className="mt-6">
              <PodcastSection />
            </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="dataviz" className="mt-6">
          <DatavizSection />
        </TabsContent>
        <TabsContent value="projets" className="mt-6">
          <ProjetsSection />
        </TabsContent>
        <TabsContent value="parcours" className="mt-6">
          <Suspense fallback={null}>
            <ParcoursSection />
          </Suspense>
        </TabsContent>
        <TabsContent value="highlights" className="mt-6">
          <HighlightsSection />
        </TabsContent>
      </Tabs>
      </Suspense>
    </section>
  );
}
